/*
 * /classes/:slug/discover/:subId? — Key Concepts "Discovery": đọc ý chính → quiz ngắn hoặc bài thực hành.
 * Quiz/thực hành chạy ở chế độ tập trung (ẩn menu trái). subId = id module (mặc định module đầu không phải "4F").
 * API: GET /api/classes/:slug/discovery → { modules[{ id, title, description, capability_code,
 *        lesson{ why, ideas[], practice_prompt }, questions[{ id, prompt, options[] }] }], models[], practice_submissions[] }
 *      POST { event_id, module_id, type: lesson_view | quiz_attempt | practice_submission, … }
 */
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Icon, SemanticIcon } from "@/components/icon"
import { EmptyState, ErrorState, LoadingState, Meter, Page, PageHeader, StatusNote, usePageTitle } from "@/components/page"
import { EditorialVisual } from "@/components/simba"
import { useClass, useFocusMode } from "@/app/ClassLayout"
import { api, behaviorSessionId, post } from "@/lib/api"
import { formatDisplayDate } from "@/lib/format"
import { cn } from "@/lib/utils"
import { Disclosure, pct } from "./community/shared"

type Mode = "lesson" | "quiz" | "practice"
type Note = { tone: "info" | "success" | "error"; text: string } | null

function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="outline" size="sm" className="self-start" onClick={onClick}>
      <Icon name="arrow-left" className="size-4" /> {label}
    </Button>
  )
}

function PracticeHistory({ practices }: { practices: any[] }) {
  if (!practices.length) return <p className="text-sm text-muted-foreground">Con chưa nộp bài thực hành nào.</p>
  return (
    <ul className="flex flex-col gap-3">
      {practices.map((s, i) => (
        <li key={s.id} className="flex flex-col gap-3 rounded-2xl border-[1.5px] border-edge p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink">Bài #{practices.length - i}</span>
            <time className="text-sm text-muted-foreground">{formatDisplayDate(s.occurred_at)}</time>
            <Badge variant={s.assessment ? "success" : "warning"} className="ml-auto">{s.assessment ? pct(s.assessment.total_score) : "Đang đánh giá"}</Badge>
          </div>
          <div>
            <p className="text-sm font-semibold text-muted-foreground">Nội dung đã lưu</p>
            <p className="text-sm break-words whitespace-pre-line text-ink">{s.content}</p>
          </div>
          {s.assessment ? (
            <div className="flex flex-col gap-3 rounded-2xl bg-sand/60 p-3">
              <p className="text-sm text-ink"><b>Kết quả theo rubric · {pct(s.assessment.total_score)}</b> — {s.assessment.summary}</p>
              <ul className="flex flex-col gap-2">
                {(s.assessment.rubric || []).map((c: any) => (
                  <li key={c.code} className="rounded-xl bg-card p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-ink">{c.name}</span>
                      <Badge>{c.level}</Badge>
                      <span className="ml-auto font-heading font-bold text-orange-strong">{c.score}/{c.max_score}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{c.feedback}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Simba đang đánh giá bài này.</p>
          )}
        </li>
      ))}
    </ul>
  )
}

function QuizStep(props: {
  module: any
  index: number
  answers: Record<string, number>
  result: any
  confidence: number
  busy: boolean
  note: Note
  onAnswer: (qid: string, opt: number) => void
  onIndex: (i: number) => void
  onConfidence: (v: number) => void
  onSubmit: () => void
  onExit: () => void
}) {
  const { module, index, answers, result, confidence, busy, note } = props
  const total = module.questions.length
  const q = module.questions[index]
  if (!q) return null
  const fb = result?.feedback?.find((f: any) => f.question_id === q.id)
  const last = index >= total - 1
  return (
    <Card>
      <CardContent className="flex flex-col gap-5">
        <BackButton label={index > 0 ? "Câu trước" : "Quay lại"} onClick={() => (index > 0 ? props.onIndex(index - 1) : props.onExit())} />
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-muted-foreground">Câu {index + 1}/{total}</p>
          <Meter value={((index + 1) / total) * 100} />
        </div>
        <h2 className="font-heading text-xl font-semibold text-ink">{q.prompt}</h2>
        <div role="radiogroup" aria-label={q.prompt} className="flex flex-col gap-2">
          {q.options.map((opt: string, oi: number) => {
            const chosen = answers[q.id] === oi
            const correct = fb?.correct_option === oi
            return (
              <button
                key={oi}
                type="button"
                role="radio"
                aria-checked={chosen}
                disabled={!!result}
                onClick={() => props.onAnswer(q.id, oi)}
                className={cn(
                  "flex items-center gap-3 rounded-2xl border-[1.5px] p-4 text-left text-base transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-default",
                  correct ? "border-success bg-success-soft" : chosen ? "border-orange bg-soft" : "border-edge bg-card hover:border-orange",
                )}
              >
                <span className={cn("grid size-6 shrink-0 place-items-center rounded-full border-2", chosen || correct ? "border-transparent bg-orange text-white" : "border-edge", correct && "bg-success")}>
                  {(chosen || correct) && <Icon name="check" className="size-3.5" />}
                </span>
                <span className="min-w-0 flex-1 break-words text-ink">{opt}</span>
              </button>
            )
          })}
        </div>
        {result ? (
          <>
            {fb?.explanation && <StatusNote tone="info">{fb.explanation}</StatusNote>}
            <Button className="self-start" onClick={() => (last ? props.onExit() : props.onIndex(index + 1))}>
              {last ? "Hoàn thành" : <>Câu tiếp theo <Icon name="arrow-right" /></>}
            </Button>
          </>
        ) : !last ? (
          <Button className="self-start" disabled={answers[q.id] == null} onClick={() => props.onIndex(index + 1)}>
            Tiếp theo <Icon name="arrow-right" />
          </Button>
        ) : (
          <>
            <label className="flex flex-col gap-2 rounded-2xl bg-sand/60 p-4">
              <span className="text-sm font-semibold text-ink">Con chắc chắn bao nhiêu? <b className="text-orange-strong">{confidence}%</b></span>
              <input type="range" min="0" max="100" step="10" value={confidence} onChange={(e) => props.onConfidence(Number(e.target.value))} className="w-full accent-orange" />
            </label>
            <Button className="self-start" disabled={busy || answers[q.id] == null} onClick={props.onSubmit}>
              {busy ? "Đang kiểm tra…" : "Xem kết quả"}
            </Button>
          </>
        )}
        {note && <StatusNote tone={note.tone}>{note.text}</StatusNote>}
      </CardContent>
    </Card>
  )
}

function PracticeStep({ module, value, busy, note, onChange, onSubmit, onExit }: { module: any; value: string; busy: boolean; note: Note; onChange: (v: string) => void; onSubmit: () => void; onExit: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-5">
        <BackButton label="Quay lại" onClick={onExit} />
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Bài thực hành</p>
        <h2 className="font-heading text-xl font-semibold text-ink">{module.lesson?.practice_prompt}</h2>
        <Textarea rows={8} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Viết suy nghĩ của con ở đây…" />
        <p className="text-sm text-muted-foreground">Viết ít nhất 30 ký tự ({value.trim().length}/30).</p>
        <Button className="self-start" disabled={busy || value.trim().length < 30} onClick={onSubmit}>
          <Icon name="send" /> {busy ? "Đang lưu…" : "Nộp bài"}
        </Button>
        {note && <StatusNote tone={note.tone}>{note.text}</StatusNote>}
      </CardContent>
    </Card>
  )
}

export default function DiscoverPage() {
  usePageTitle("Key Concepts", "Khám phá ý tưởng cốt lõi, làm quiz ngắn và bài thực hành.")
  const { slug, base } = useClass()
  const { subId } = useParams()
  const [data, setData] = useState<any>()
  const [error, setError] = useState("")
  const [mode, setMode] = useState<Mode>("lesson")
  const [quizIndex, setQuizIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [confidence, setConfidence] = useState(70)
  const [practice, setPractice] = useState("")
  const [result, setResult] = useState<any>(null)
  const [busy, setBusy] = useState("")
  const [note, setNote] = useState<Note>(null)
  useFocusMode(mode !== "lesson")

  const path = `/api/classes/${encodeURIComponent(slug)}/discovery`
  const load = () => api(path).then(setData)
  const firstLoad = () => {
    setError("")
    load().catch((e: any) => setError(e.message))
  }
  useEffect(() => {
    setMode("lesson")
    firstLoad()
  }, [slug])

  const modules: any[] = data?.modules || []
  const active = modules.find((m) => m.id === subId) || modules.find((m) => !String(m.title).toLowerCase().includes("4f"))
  const model = data?.models?.find((m: any) => m.capability_code === active?.capability_code)
  const practices = (data?.practice_submissions || []).filter((s: any) => s.module_id === active?.id)

  // Đổi module: về màn bài học, xoá câu trả lời cũ.
  useEffect(() => {
    setMode("lesson")
    setAnswers({})
    setResult(null)
    setQuizIndex(0)
    setNote(null)
  }, [active?.id])

  // Ghi lesson_view một lần mỗi phiên cho mỗi module (giữ hành vi cũ).
  useEffect(() => {
    if (!active) return
    const key = `simba-discovery-view:${behaviorSessionId()}:${slug}:${active.id}`
    try {
      if (sessionStorage.getItem(key)) return
      sessionStorage.setItem(key, "1")
    } catch {}
    post(path, { event_id: crypto.randomUUID(), module_id: active.id, type: "lesson_view" })
      .then((res: any) => {
        if (!res?.model) return
        setData((cur: any) => ({
          ...cur,
          models: [...(cur.models || []).filter((m: any) => m.capability_code !== res.model.capability_code), res.model],
        }))
      })
      .catch(() => {})
  }, [active?.id, slug])

  if (error) return <Page><ErrorState message={error} onRetry={firstLoad} /></Page>
  if (!data) return <LoadingState label="Đang mở Key Concepts…" />
  if (!active)
    return (
      <Page>
        <PageHeader eyebrow="Key Concepts" title="Chưa có nội dung" />
        <EmptyState mascot="elephant-thinking" title="Mentor sẽ cập nhật Key Concepts tại đây." />
      </Page>
    )

  const exitToLesson = () => {
    setMode("lesson")
    setResult(null)
  }
  const submitQuiz = async () => {
    if (active.questions.some((q: any) => answers[q.id] == null)) {
      setNote({ tone: "error", text: "Con hãy trả lời đủ các câu trước nhé." })
      return
    }
    setBusy("quiz")
    setNote(null)
    try {
      const res: any = await post(path, {
        event_id: crypto.randomUUID(),
        module_id: active.id,
        type: "quiz_attempt",
        confidence: confidence / 100,
        answers: Object.entries(answers).map(([question_id, option]) => ({ question_id, option })),
      })
      setResult(res)
      setNote({ tone: "success", text: `Con trả lời đúng ${Math.round(res.score * active.questions.length)}/${active.questions.length} câu. Simba đã ghi nhận tiến bộ của con.` })
      await load()
    } catch (e: any) {
      setNote({ tone: "error", text: e.message })
    } finally {
      setBusy("")
    }
  }
  const submitPractice = async () => {
    setBusy("practice")
    setNote(null)
    try {
      const res: any = await post(path, { event_id: crypto.randomUUID(), module_id: active.id, type: "practice_submission", confidence: confidence / 100, content: practice })
      setPractice("")
      setNote({
        tone: "success",
        text: res?.assessment
          ? `Đã lưu bài và chấm theo rubric: ${pct(res.assessment.total_score)}. Con có thể xem lại ở “Bài thực hành đã nộp”.`
          : "Đã lưu bài thực hành. Simba đang chuẩn bị kết quả đánh giá.",
      })
      setMode("lesson")
      await load()
    } catch (e: any) {
      setNote({ tone: "error", text: e.message })
    } finally {
      setBusy("")
    }
  }

  if (mode === "quiz")
    return (
      <Page className="max-w-[720px]">
        <QuizStep
          module={active}
          index={quizIndex}
          answers={answers}
          result={result}
          confidence={confidence}
          busy={busy === "quiz"}
          note={note}
          onAnswer={(qid, opt) => setAnswers((cur) => ({ ...cur, [qid]: opt }))}
          onIndex={setQuizIndex}
          onConfidence={setConfidence}
          onSubmit={submitQuiz}
          onExit={exitToLesson}
        />
      </Page>
    )
  if (mode === "practice")
    return (
      <Page className="max-w-[720px]">
        <PracticeStep module={active} value={practice} busy={busy === "practice"} note={note} onChange={setPractice} onSubmit={submitPractice} onExit={exitToLesson} />
      </Page>
    )

  const choices = modules.filter((m) => !String(m.title).toLowerCase().includes("4f"))
  return (
    <Page className="max-w-[880px]">
      <div className="grid items-center gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)]">
        <div className="flex flex-col gap-4">
          <PageHeader eyebrow="Key Concepts" title={active.title} description={active.description} />
          <div className="flex flex-wrap items-center gap-2">
            <span className="grid size-10 place-items-center rounded-2xl bg-soft"><SemanticIcon label={active.title} className="size-5" /></span>
            {model && <Badge variant="solid">{pct(model.mastery)} thành thạo</Badge>}
          </div>
        </div>
        <EditorialVisual kind="concepts" alt="Không gian khám phá các ý tưởng cốt lõi bằng mô hình giấy và kính quan sát" className="hidden md:block" />
      </div>

      {choices.length > 1 && (
        <nav aria-label="Chọn chủ đề" className="flex flex-wrap gap-2">
          {choices.map((m) => (
            <Button key={m.id} size="sm" variant={m.id === active.id ? "secondary" : "outline"} nativeButton={false} render={<Link to={`${base}/discover/${m.id}`} />}>
              {m.title}
            </Button>
          ))}
        </nav>
      )}

      {note && <StatusNote tone={note.tone}>{note.text}</StatusNote>}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm font-semibold uppercase tracking-wide text-orange-strong">Key concept là gì?</p>
          <p className="text-lg leading-relaxed text-ink">{active.lesson?.why}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button disabled={!active.questions?.length} onClick={() => { setMode("quiz"); setQuizIndex(0); setNote(null) }}>
              Làm quiz ngắn <Icon name="arrow-right" />
            </Button>
            <Button variant="ghost" onClick={() => { setMode("practice"); setNote(null) }}>
              Hoặc làm bài thực hành
            </Button>
          </div>
        </CardContent>
      </Card>

      {!!active.lesson?.ideas?.length && (
        <Disclosure title="Khám phá thêm">
          <ol className="flex flex-col gap-3">
            {active.lesson.ideas.map((idea: string, i: number) => (
              <li key={i} className="flex items-start gap-3">
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-soft text-sm font-bold text-orange-strong">{i + 1}</span>
                <p className="text-ink">{idea}</p>
              </li>
            ))}
          </ol>
        </Disclosure>
      )}

      {model && (
        <Disclosure title="Tiến bộ của con">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2"><p className="text-sm text-ink"><b className="font-heading text-2xl text-orange-strong">{pct(model.mastery)}</b> Thành thạo</p><Meter value={model.mastery * 100} /></div>
            <div className="flex flex-col gap-2"><p className="text-sm text-ink"><b className="font-heading text-2xl text-orange-strong">{pct(model.confidence)}</b> Chắc chắn</p><Meter value={model.confidence * 100} /></div>
          </div>
        </Disclosure>
      )}

      <Disclosure title={`Bài thực hành đã nộp (${practices.length})`}>
        <PracticeHistory practices={practices} />
      </Disclosure>
    </Page>
  )
}
